import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles, Bot } from "lucide-react";
import { API } from "@/lib/api";

// Simple markdown-to-HTML for **bold**, *italic*, `code`, links, and bullets
function formatMarkdown(text: string): string {
  let html = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Convert **bold** first (greedy-safe)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="text-edx-cyan">$1</strong>');

  // Inline code
  html = html.replace(/`([^`]+?)`/g, '<code class="px-1 py-0.5 rounded bg-white/10 font-mono text-[12px] text-edx-pink">$1</code>');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-edx-cyan underline hover:text-edx-pink">$1</a>');

  // Normalize: " * item * item" inline lists → newline-delimited
  // Replace " * " between non-newline content with newline + "- "
  html = html.replace(/(?:^|\s)\*\s+/g, "\n- ");

  // Now turn lines starting with "- " or "• " into <ul><li>
  const lines = html.split("\n");
  const out: string[] = [];
  let inUl = false;
  for (const ln of lines) {
    const m = ln.match(/^\s*[-•]\s+(.*)$/);
    if (m) {
      if (!inUl) { out.push('<ul class="list-disc list-inside space-y-1 my-2 pl-1">'); inUl = true; }
      out.push(`<li>${m[1]}</li>`);
    } else {
      if (inUl) { out.push("</ul>"); inUl = false; }
      if (ln.trim()) out.push(`<p class="my-1">${ln}</p>`);
    }
  }
  if (inUl) out.push("</ul>");
  return out.join("");
}

function getSessionId(): string {
  let id = localStorage.getItem("edx_chat_session");
  if (!id) {
    id = "edx_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("edx_chat_session", id);
  }
  return id;
}

interface Msg { role: "user" | "assistant"; content: string; }

export default function NovaChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([
    { role: "assistant", content: "Hi 👋 I'm **NOVA** — EDENOX's AI assistant. Ask me anything about our services, pricing, or describe your project and I'll recommend the perfect package." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (override?: string) => {
    const text = (override ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const contentType = res.headers.get("content-type") || "";
      if (!res.ok || !res.body || !contentType.includes("text/event-stream")) {
        throw new Error(`NOVA API returned ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const obj = JSON.parse(line.slice(6));
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            }
            if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: obj.error || "NOVA is temporarily unavailable. Please try again shortly." };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "NOVA cannot reach the EDENOX service right now. Please try again shortly or use the consultation form." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const quick = [
    "What's the cost for a small business website?",
    "I need a mobile app — what should I expect?",
    "Tell me about EDENOX",
    "Help me register a company",
  ];

  return (
    <>
      <button
        type="button"
        data-testid="nova-launcher"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`fixed bottom-24 right-6 z-[60] transition-transform hover:scale-105 active:scale-95 ${open ? "hidden" : ""}`}
        style={{ cursor: "pointer", pointerEvents: "auto" }}
        aria-label="Open NOVA"
        aria-expanded={open}
      >
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-edx-pink blur-xl opacity-60 animate-edx-pulse" />
          <div className="relative h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-gradient-to-br from-edx-cyan via-edx-purple to-edx-pink p-[2px]">
            <div className="h-full w-full rounded-full bg-edx-space flex items-center justify-center">
              <Sparkles className="text-white" size={22} />
            </div>
          </div>
          <div className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-edx-green ring-2 ring-edx-space" />
        </div>
      </button>

      <AnimatePresence mode="wait">
        {open && (
          <motion.div
            key="nova-panel"
            data-testid="nova-panel"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className="nova-panel glass glass-edge flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-edx-cyan via-edx-purple to-edx-pink p-[2px]">
                    <div className="h-full w-full rounded-full bg-edx-space flex items-center justify-center">
                      <Bot size={18} className="text-white" />
                    </div>
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-edx-green ring-2 ring-edx-eclipse" />
                </div>
                <div>
                  <div className="font-display font-bold text-white text-sm">NOVA</div>
                  <div className="text-[10px] uppercase tracking-widest text-edx-cyan font-mono">
                    EDENOX AI assistant
                  </div>
                </div>
              </div>
              <button data-testid="nova-close" onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-white/5">
                <X size={16} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="nova-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`nova-msg-${m.role}-${i}`}
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-gradient-to-br from-edx-blue to-edx-purple text-white rounded-br-sm whitespace-pre-wrap"
                        : "bg-white/5 text-white rounded-bl-sm border border-white/5"
                    }`}
                  >
                    {m.role === "assistant" && m.content ? (
                      <div dangerouslySetInnerHTML={{ __html: formatMarkdown(m.content) }} />
                    ) : m.content || (streaming && i === messages.length - 1 ? (
                      <span className="inline-flex gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-edx-cyan animate-edx-pulse" />
                        <span className="h-1.5 w-1.5 rounded-full bg-edx-purple animate-edx-pulse" style={{ animationDelay: "0.15s" }} />
                        <span className="h-1.5 w-1.5 rounded-full bg-edx-pink animate-edx-pulse" style={{ animationDelay: "0.3s" }} />
                      </span>
                    ) : "")}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {quick.map((q) => (
                    <button key={q} data-testid={`nova-quick-${q.slice(0, 8)}`}
                      onClick={() => send(q)}
                      className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 bg-white/5 hover:bg-edx-cyan/10 hover:border-edx-cyan text-edx-muted hover:text-white transition-all">
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 border-t border-white/5">
              <div className="flex items-center gap-2 bg-edx-space/60 border border-white/10 rounded-xl px-3">
                <input
                  data-testid="nova-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                  placeholder="Ask NOVA anything..."
                  className="flex-1 bg-transparent outline-none text-sm py-3 placeholder:text-edx-muted"
                  disabled={streaming}
                />
                <button data-testid="nova-send" onClick={() => send()} disabled={!input.trim() || streaming}
                  className="h-8 w-8 rounded-lg bg-gradient-to-br from-edx-cyan to-edx-purple flex items-center justify-center disabled:opacity-40">
                  <Send size={14} />
                </button>
              </div>
              <div className="mt-2 text-[10px] text-edx-muted text-center font-mono">
                Innovation, Fortified. NOVA powered by EDENOX.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
